import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, Check, Mail, Facebook, Twitter, MessageCircle, Linkedin } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@workspace/ui/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";

interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  url: string;
  title: string;
}

/**
 * Best-effort copy-to-clipboard fallback for non-secure contexts (HTTP,
 * older mobile webviews) and browsers where `navigator.clipboard` is
 * missing or rejects. Uses the legacy `document.execCommand("copy")` on
 * a temporary, off-screen <textarea>. Returns true when the copy
 * succeeded so the caller can decide whether to surface the toast.
 */
function legacyCopyToClipboard(text: string): boolean {
  if (typeof document === "undefined") return false;
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.top = "-1000px";
  ta.style.left = "-1000px";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  try {
    ta.focus();
    ta.select();
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    document.body.removeChild(ta);
  }
}

export function ShareDialog({ open, onOpenChange, url, title }: ShareDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const urlInputRef = useRef<HTMLInputElement | null>(null);

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  // Reset the "Copied" badge after 2s, but keyed on a `useEffect` so the
  // timer is cleared if the dialog is unmounted mid-window. The previous
  // bare `setTimeout` fired `setCopied(false)` on an unmounted component
  // when the user closed the dialog within those 2s — React then logs a
  // "set state on unmounted component" warning in development.
  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(id);
  }, [copied]);

  // Reset the failed-copy fallback whenever the dialog reopens, so users
  // who later move to a secure context don't see the fallback indefinitely.
  useEffect(() => {
    if (!open) setCopyFailed(false);
  }, [open]);

  const handleCopy = async () => {
    // Feature-detect first: in HTTP / insecure contexts `navigator.clipboard`
    // is undefined entirely, and even when it exists `writeText` may reject
    // (permissions, focus issues, Safari quirks). Fall back to execCommand,
    // and if that also fails, surface a selectable text input the user can
    // copy from manually instead of just toasting "share failed".
    const hasAsyncClipboard =
      typeof navigator !== "undefined" &&
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === "function" &&
      (typeof window === "undefined" || window.isSecureContext !== false);

    if (hasAsyncClipboard) {
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        toast({ title: t("carDetail.shareLinkCopied") });
        return;
      } catch {
        // fall through to the legacy path
      }
    }

    if (legacyCopyToClipboard(url)) {
      setCopied(true);
      toast({ title: t("carDetail.shareLinkCopied") });
      return;
    }

    // Last resort: preselect the URL in the visible text input so the user
    // can copy it themselves with the OS shortcut.
    setCopyFailed(true);
    if (urlInputRef.current) {
      urlInputRef.current.focus();
      urlInputRef.current.select();
    }
    toast({
      title: t("carDetail.shareCopyManualTitle", {
        defaultValue: "Copy the link manually",
      }),
      description: t("carDetail.shareCopyManualDesc", {
        defaultValue: "Your browser blocked the copy. Press Ctrl/Cmd+C to copy the selected link.",
      }),
    });
  };

  const options: Array<{ key: string; label: string; href: string; icon: typeof Mail }> = [
    {
      key: "email",
      label: "Email",
      href: `mailto:?subject=${encodedTitle}&body=${encodedTitle}%20%E2%80%94%20${encodedUrl}`,
      icon: Mail,
    },
    {
      key: "whatsapp",
      label: "WhatsApp",
      href: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
      icon: MessageCircle,
    },
    {
      key: "facebook",
      label: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      icon: Facebook,
    },
    {
      key: "twitter",
      label: "X",
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      icon: Twitter,
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      icon: Linkedin,
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("common.share")}</DialogTitle>
          <DialogDescription className="line-clamp-1">{title}</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2">
          <input
            ref={urlInputRef}
            type="text"
            readOnly
            value={url}
            onFocus={(e) => e.currentTarget.select()}
            className="flex-1 truncate bg-transparent text-sm text-muted-foreground outline-none"
            aria-label={t("carDetail.shareUrlLabel", { defaultValue: "Share URL" })}
          />
          <button
            type="button"
            onClick={handleCopy}
            className="cursor-pointer flex shrink-0 items-center gap-1.5 rounded-md bg-foreground px-3 py-1.5 text-xs font-semibold text-white hover:bg-foreground/90 transition-colors"
            aria-label={t("carDetail.shareCopyLink", { defaultValue: "Copy link" })}
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied
              ? t("carDetail.shareCopied", { defaultValue: "Copied" })
              : t("carDetail.shareCopyLink", { defaultValue: "Copy link" })}
          </button>
        </div>
        {copyFailed ? (
          <p className="-mt-2 text-xs text-muted-foreground">
            {t("carDetail.shareCopyManualHint", {
              defaultValue: "Press Ctrl/Cmd+C to copy the selected link above.",
            })}
          </p>
        ) : null}

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {options.map((opt) => {
            const Icon = opt.icon;
            return (
              <a
                key={opt.key}
                href={opt.href}
                target={opt.key === "email" ? undefined : "_blank"}
                rel="noopener noreferrer"
                className="cursor-pointer flex items-center gap-2 rounded-lg border border-border px-3 py-2.5 text-sm font-medium text-foreground hover:bg-muted transition-colors"
              >
                <Icon className="h-4 w-4 text-muted-foreground" />
                {opt.label}
              </a>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
